// LINE ITEM GROUPS (owner, 2026-10-20 to 2026-10-22 — docs/decisions.md D-396
// to D-403). The rules the server enforces and the screens show, kept once so
// what a person sees is what the server enforces.
//
// A GROUP ROW (`line_kind = 'group'`) stands over one or more items (one since D-423)
// (`group_id` names it). It is what a reviewer sees collapsed and what the
// Point Click Care CSV exports; its items stay active below it.
//
// THREE ROW SETS, and every reader of an invoice's lines uses exactly one of
// them (D-403), through the helpers below and never ad hoc:
//
//   * REAL LINES — active items: amounts, flags, the coding gates, analytics,
//     Memory. A group row is never one: its values are copies of its lines'.
//   * EXPORTED ROWS — active rows standing alone (`group_id` empty): a group
//     row and every item outside a group. The CSV and its tax and shipping
//     spread read these.
//   * DISPLAY — the line table: groups with their items nested under them.
//
// The sum of the real lines equals the sum of the exported rows by
// construction (a group row's total is its lines' sum, G-6).

import { CHARGE_KIND, LINE_KIND, type ChargeKind, type LineKind } from './enums.js'
import { formatMoney, parseMoney, type MoneyString } from './money.js'

/** The fields of a row the three sets are told apart by. A row from before
    line groups carries neither column, and reads as an item standing alone. */
export interface GroupedRow {
  is_active: boolean
  line_kind?: LineKind | null
  group_id?: string | null
  /** A charge group's kind (D-420); null on every other row. */
  charge_kind?: ChargeKind | null
}

/** A group row (G-5): the explicit marker, never inferred from links. */
export function isGroupRow(row: Pick<GroupedRow, 'line_kind'>): boolean {
  return row.line_kind === LINE_KIND.group
}

/** A REAL LINE: an active item, in a group or not. */
export function isRealLine(row: GroupedRow): boolean {
  return row.is_active && !isGroupRow(row)
}

/** A CHARGE GROUP's row (D-420): the invoice's Tax or Shipping group. */
export function isChargeGroup(row: Pick<GroupedRow, 'line_kind' | 'charge_kind'>): boolean {
  return isGroupRow(row) && (row.charge_kind ?? null) !== null
}

/**
 * An EXPORTED ROW: an active row standing alone — a group row, or an item in
 * no group. A charge group is not one (D-420): its sum is spread over the
 * exported rows instead, as the old Tax and Shipping fields were.
 */
function isExportedRow(row: GroupedRow): boolean {
  return row.is_active && (row.group_id ?? null) === null && !isChargeGroup(row)
}

/** The real lines of a set, in their order. */
export function realLines<T extends GroupedRow>(rows: readonly T[]): T[] {
  return rows.filter(isRealLine)
}

/** The exported rows of a set, in their order. */
export function exportedRows<T extends GroupedRow>(rows: readonly T[]): T[] {
  return rows.filter(isExportedRow)
}

/** The active items under one group row. */
export function linesOfGroup<T extends GroupedRow>(rows: readonly T[], groupId: string): T[] {
  return rows.filter((row) => isRealLine(row) && row.group_id === groupId)
}

/** The active charge group row of one kind on an invoice, if it has been made (D-420). */
export function chargeGroupOf<T extends GroupedRow & { id: string }>(rows: readonly T[], kind: ChargeKind): T | undefined {
  return rows.find((row) => row.is_active && isGroupRow(row) && row.charge_kind === kind)
}

/** The ids of an invoice's active charge group rows (D-420). */
function chargeGroupIds<T extends GroupedRow & { id: string }>(rows: readonly T[]): Set<string> {
  return new Set(rows.filter((row) => row.is_active && isChargeGroup(row)).map((row) => row.id))
}

/**
 * A CHARGE LINE (D-420): a real line in the Tax or Shipping group. It carries
 * no Account, Department or Marketing Category, and no coding gate asks for one.
 */
export function chargeLineIds<T extends GroupedRow & { id: string }>(rows: readonly T[]): Set<string> {
  const groups = chargeGroupIds(rows)
  return new Set(rows.filter((row) => isRealLine(row) && row.group_id != null && groups.has(row.group_id)).map((row) => row.id))
}

/**
 * The CODED LINES: the real lines the coding gates and the Account columns
 * read — every real line but a charge line (D-420).
 */
export function codedLines<T extends GroupedRow & { id: string }>(rows: readonly T[]): T[] {
  const charges = chargeLineIds(rows)
  return realLines(rows).filter((row) => !charges.has(row.id))
}

/**
 * THE TWO AMOUNTS THE CSV SPREADS (D-420): the invoice's tax and shipping,
 * each its old header field (an invoice from before D-420 may hold one; a new
 * one holds zero) plus the sum of its charge group's lines. Exact cents.
 */
export function spreadCharges<T extends GroupedRow & { id: string; total?: MoneyString | null }>(
  header: { tax: MoneyString | null; shipping: MoneyString | null },
  rows: readonly T[],
): { tax: MoneyString; shipping: MoneyString } {
  const sum = (kind: ChargeKind, start: MoneyString | null): MoneyString => {
    const group = chargeGroupOf(rows, kind)
    let cents = parseMoney(start) ?? 0
    if (group) for (const line of linesOfGroup(rows, group.id)) cents += parseMoney(line.total ?? null) ?? 0
    return formatMoney(cents)
  }
  return { tax: sum(CHARGE_KIND.tax, header.tax), shipping: sum(CHARGE_KIND.shipping, header.shipping) }
}

/**
 * Where an exported row sorts (review finding 11): a group row at its first
 * line's number, any other row at its own. The CSV, its tax and shipping
 * spread, and the GL Summary's tie-break all order by this.
 */
export function exportPosition<T extends GroupedRow & { id: string; line_number: number }>(
  rows: readonly T[],
  row: T,
): number {
  if (!isGroupRow(row)) return row.line_number
  const numbers = linesOfGroup(rows, row.id).map((line) => line.line_number)
  return numbers.length === 0 ? row.line_number : Math.min(...numbers)
}

// ---------------------------------------------------------------------------
// A group's three values (G-6, G-8, G-9)
// ---------------------------------------------------------------------------

/** The three values a group shows as its own and its lines must agree on. */
const GROUP_FIELDS = ['account_id', 'department_id', 'marketing_category_id'] as const
export type GroupField = (typeof GROUP_FIELDS)[number]

/**
 * How a group's field reads from its lines' values (G-8, as the owner restated
 * it on 2026-10-22 — D-406: "Let the locked fields show as null with the
 * appropriate border color - blocking release - until all child rows agree.
 * Once all child rows agree, you can show the agreed-upon Account value"):
 *
 *   * `agree`      — every line holds the same value: the value, no border;
 *   * `some_empty` — some lines are empty and the rest agree: blank, red;
 *   * `disagree`   — two lines hold different values: blank, red;
 *   * `all_empty`  — no line holds a value: blank, red.
 *
 * Until D-406 `some_empty` showed the value the others agreed on.
 */
type GroupFieldState = 'agree' | 'some_empty' | 'disagree' | 'all_empty'

interface GroupFieldView {
  state: GroupFieldState
  /** What the group's field shows: the value every line holds, else null (D-406). */
  value: string | null
  /** The red border (G-8): on every state but `agree`. */
  red: boolean
}

function present(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

/** G-8, for one field: the lines' values, in any order. */
export function groupFieldView(values: readonly (string | null | undefined)[]): GroupFieldView {
  const filled = values.map(present).filter((value): value is string => value !== null)
  const distinct = new Set(filled)
  if (distinct.size > 1) return { state: 'disagree', value: null, red: true }
  if (distinct.size === 0) return { state: 'all_empty', value: null, red: true }
  const value = [...distinct][0] as string
  if (filled.length < values.length) return { state: 'some_empty', value: null, red: true }
  return { state: 'agree', value, red: false }
}

/**
 * G-6: the value the server COPIES onto the group row — the shared value when
 * every line agrees, nothing otherwise: the same value the screen shows (D-406).
 */
export function groupCopy(values: readonly (string | null | undefined)[]): string | null {
  const view = groupFieldView(values)
  return view.state === 'agree' ? view.value : null
}

/**
 * G-9, as D-406 restates it: the lines do not all hold the same value — two
 * name different values, or some are empty and the rest are not. Blocks
 * release, approval, Typed Into Point Click Care and export, as a line missing
 * half its code does (D-9). A field empty on EVERY line is not this gate's: an
 * empty half is gated as on any line, and a Marketing Category no line owes is
 * empty on all of them. Until D-406 only two different values counted.
 */
function groupFieldDisagrees(values: readonly (string | null | undefined)[]): boolean {
  const state = groupFieldView(values).state
  return state === 'disagree' || state === 'some_empty'
}

/** One line's three values, however the caller holds them. */
type GroupFieldValues = Readonly<Record<GroupField, string | null | undefined>>

/** G-8 for all three fields of one group. */
export function groupFieldViews(lines: readonly GroupFieldValues[]): Record<GroupField, GroupFieldView> {
  return {
    account_id: groupFieldView(lines.map((line) => line.account_id)),
    department_id: groupFieldView(lines.map((line) => line.department_id)),
    marketing_category_id: groupFieldView(lines.map((line) => line.marketing_category_id)),
  }
}

/** G-9 for one group: any of its three fields is not held alike by every line (D-406). */
export function groupDisagrees(lines: readonly GroupFieldValues[]): boolean {
  return GROUP_FIELDS.some((field) => groupFieldDisagrees(lines.map((line) => line[field])))
}

/**
 * G-6: the group row's price and total — the sum of its lines' totals, in exact
 * cents. A line with no total adds nothing; a group none of whose lines has a
 * total has none.
 */
export function groupTotal(lineTotals: readonly (MoneyString | null | undefined)[]): MoneyString | null {
  let sum = 0
  let known = false
  for (const raw of lineTotals) {
    const cents = parseMoney(raw ?? null)
    if (cents === null) continue
    sum += cents
    known = true
  }
  return known ? formatMoney(sum) : null
}

/**
 * The ids of the groups in a set whose lines do not agree (G-9, D-406), for the
 * gate's refusal. A charge group is never one (D-420): its lines carry no values.
 */
export function disagreeingGroupIds<T extends GroupedRow & { id: string }>(
  rows: readonly T[],
  valuesOf: (row: T) => GroupFieldValues,
): string[] {
  const out: string[] = []
  for (const group of rows.filter((row) => row.is_active && isGroupRow(row) && !isChargeGroup(row))) {
    if (groupDisagrees(linesOfGroup(rows, group.id).map(valuesOf))) out.push(group.id)
  }
  return out
}

// ---------------------------------------------------------------------------
// A group's Associated Entities (owner, 2026-10-28 — D-425)
// ---------------------------------------------------------------------------

/** The fields of an `associated_entities` row the group rule compares. */
export interface GroupTagInput {
  entity_type: string
  vehicle_id?: string | null
  staff_upn?: string | null
  entity_name?: string | null
  entity_source_text?: string | null
  is_active?: boolean
}

function normalizedText(value: string | null | undefined): string {
  return (value ?? '').trim().replace(/\s+/g, ' ').toLowerCase()
}

/**
 * ONE TAG, AS THE GROUP RULE COMPARES IT (D-425): its entity type and the
 * entity it is matched to (`vehicle_id`, or `staff_upn` without case). A tag matched to
 * nothing — a Participant, or a Vehicle or Staff tag not yet matched —
 * compares by its type and its printed text, its name falling back to the
 * document text, case and spacing ignored.
 */
export function groupTagKey(tag: GroupTagInput): string {
  const reference = (tag.vehicle_id ?? '').trim() || (tag.staff_upn ?? '').trim().toLowerCase()
  if (reference !== '') return `${tag.entity_type}|ref|${reference}`
  const printed = normalizedText(tag.entity_name) || normalizedText(tag.entity_source_text)
  return `${tag.entity_type}|text|${printed}`
}

/**
 * A line's active tags as one set, written as a sorted key. Exported for the
 * match worker's grouping checks (D-428), so a group the server makes obeys the
 * same rule the gate applies.
 */
export function lineTagSetKey(tags: readonly GroupTagInput[]): string {
  const keys = new Set(tags.filter((tag) => tag.is_active !== false).map(groupTagKey))
  return [...keys].sort().join('\n')
}

/**
 * D-425 (owner, 2026-10-28: "Associated Entities need to be consistent for all
 * rows in a group"): the lines of a group do not all carry the same set of
 * tags. One line with a tag and another with none disagree; lines with no tags
 * at all agree. Blocks release, approval, Typed Into Point Click Care and
 * export, as `group_entities_disagree`.
 */
export function groupTagsDisagree(lineTags: readonly (readonly GroupTagInput[])[]): boolean {
  return new Set(lineTags.map(lineTagSetKey)).size > 1
}

/**
 * The tags every line of a group carries, one per entity — what the group
 * row's Additional Detail lists. Empty when the lines disagree: the group then
 * shows none, and is red.
 */
export function sharedGroupTags<T extends GroupTagInput>(lineTags: readonly (readonly T[])[]): T[] {
  if (lineTags.length === 0 || groupTagsDisagree(lineTags)) return []
  const seen = new Set<string>()
  const out: T[] = []
  for (const tag of (lineTags[0] ?? []).filter((t) => t.is_active !== false)) {
    const key = groupTagKey(tag)
    if (seen.has(key)) continue
    seen.add(key)
    out.push(tag)
  }
  return out
}

/**
 * The ids of the groups in a set whose lines do not carry the same tags
 * (D-425), for the gate's refusal. A charge group is never one: the Tax and
 * Shipping groups carry no coding, and their lines' tags may differ (D-420).
 * `tags` are the invoice's active tags; one with no line is not a line's.
 */
export function entityDisagreeingGroupIds<T extends GroupedRow & { id: string }>(
  rows: readonly T[],
  tags: readonly (GroupTagInput & { child_id: string | null })[],
): string[] {
  const byLine = new Map<string, GroupTagInput[]>()
  for (const tag of tags) {
    if (tag.child_id === null || tag.is_active === false) continue
    byLine.set(tag.child_id, [...(byLine.get(tag.child_id) ?? []), tag])
  }
  const out: string[] = []
  for (const group of rows.filter((row) => row.is_active && isGroupRow(row) && !isChargeGroup(row))) {
    if (groupTagsDisagree(linesOfGroup(rows, group.id).map((line) => byLine.get(line.id) ?? []))) out.push(group.id)
  }
  return out
}

/**
 * A GROUP WITH NO NAME (owner, 2026-10-22 — D-406): a line dragged onto a line
 * standing alone makes a group of the two whose Description is empty and
 * required. It blocks release, approval, Typed Into Point Click Care and export
 * until it is named, as `unnamed_group`. Matching never makes one.
 */
function isUnnamedGroup(row: GroupedRow & { description?: string | null }): boolean {
  return row.is_active && isGroupRow(row) && (row.description ?? '').trim() === ''
}

/** The ids of the unnamed group rows in a set (D-406), for the gate's refusal. */
export function unnamedGroupIds<T extends GroupedRow & { id: string; description?: string | null }>(rows: readonly T[]): string[] {
  return rows.filter(isUnnamedGroup).map((row) => row.id)
}

/**
 * HOW A LINE'S DESCRIPTION MEETS A SPECIFIC GROUPING RULE (owner, 2026-10-26 —
 * D-418). Both sides are reduced to this key and compared: lower-cased,
 * whitespace collapsed and trimmed, ONE trailing parenthetical note removed
 * (`DRINK,BOOST,PLUS,VERY,VANILLA,8OZ (HCPCS Code #: B4152)` meets the rule
 * `DRINK,BOOST,PLUS,VERY,VANILLA,8OZ`), and the spaces around each comma
 * removed. The Postgres twin of this function, applied to the rule's stored
 * description, is `groupingDescriptionKeySql` in `backend/src/repo/pg/grouping.ts`;
 * the mock's `specificHits` calls this one. A real-Postgres test holds the two
 * equal (`backend/src/repo/pg/liveReads.test.ts`).
 */
export function groupingDescriptionKey(text: string | null | undefined): string {
  return (text ?? '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\s*\([^()]*\)$/, '')
    .replace(/\s*,\s*/g, ',')
    .trim()
}
